import React, { useState, useRef, useEffect } from 'react';
import { useForm, useWatch } from "react-hook-form"
import * as XLSX from 'xlsx';
import { Box, CircularProgress, Typography } from '@mui/joy';
import Previewer from './Previewer';
import { useTranslation } from 'react-i18next';
import { Input, Text } from 'src/mtska-frontend-app-component';
import { useDataProvider } from 'src/mtska-frontend-data-provider';
import { XMLParser } from 'fast-xml-parser';

import { Stepper, Step, StepLabel, Alert } from '@mui/material';
import { useXMLFlattener } from './XMLFlattener';
import { useXMLMapping } from './XMLMapping';
import WidgetDocumentPreview from 'src/mtska-frontend-app-component/components/FormElements/WidgetDocumentPreview';
import Constants from './Constants';
import JSONPretty from 'react-json-pretty'

const ImporterFileMappingConstants = ({ dataProviderName, configDataProviderName, uid, handleClose, filters, externalDefaultMapping = {}, externalDefaultConstants = [], externalSetForwardDisabled, externalSetBackDisabled, externalSetActionInvoked, externalSetActionLabel, externalSetErrorMessage, actionInvoked, externalFinalMessage, skipConstantsStep = false, externalFinalSave, externalFinalSaveThen }) => {

    const steps = ['Import file', 'Handle mapping', 'Completion'];
    if (!skipConstantsStep) {
        steps.splice(2, 0, 'Handle constants');
    }
    const [activeStep, setActiveStep] = useState(0);

    const { t } = useTranslation();
    const { item: config } = useDataProvider('configs/config');
    
    const { save: saveImport, errorMessage } = useDataProvider('importer/file', true);

    const { loadAll: loadMappings, items: mappingsDb } = useDataProvider('importer/mapping');
    const { flattenItalianInvoiceXml } = useXMLFlattener();
    const { mapItalianInvoiceXml } = useXMLMapping();

    const [excelFile, setExcelFile] = useState(null);
    const [pdfFile, setPdfFile] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [xmlFile, setXmlFile] = useState(null);
    const [error, setError] = useState(null);
    const [dataType, setDataType] = useState(null);
    const [data, setData] = useState(null);
    const [editing, setEditing] = useState(false);
    const [mapping, setMapping] = useState({});
    const [constants, setConstants] = useState({});
    const [selectedMapping, setSelectedMapping] = useState();
    const [defaultMapping, setDefaultMapping] = useState(externalDefaultMapping);
    const [defaultConstants, setDefaultConstants] = useState(externalDefaultConstants);
    const [selectedMappingNewName, setSelectedMappingNewName] = useState('');
    const [createCopy, setCreateCopy] = useState();
    const [model, setModel] = useState();
    const [mappings, setMappings] = useState({});
    const [attemptedSteps, setAttemptedSteps] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [validationErrorState, setValidationErrorState] = useState(null);
    const [actionResult, setActionResult] = useState(null);

    const isFirstRender = useRef(true);

    const { handleSubmit, control, setValue } = useForm();
    const selectedMappingNewNameWatcher = useWatch({ control, name: 'selectedMappingNewName' });
    const selectedMappingWatcher = useWatch({ control, name: 'selectedMapping' });
    const createCopyWatcher = useWatch({ control, name: 'createCopy' });
    const selectedFileWatcher = useWatch({ control, name: 'fileUploader' });

    useEffect(() => {
        if (!Array.isArray(mappingsDb) || mappingsDb.length === 0) return;
        setMappings((prevMappings) => {
            const mappingValues = mappingsDb.reduce((a, v) => ({ ...a, [v.name]: v.mapping }), {});
            if (JSON.stringify(prevMappings) !== JSON.stringify(mappingValues)) {
                return mappingValues;
            }
            return prevMappings;
        });
    }, [mappingsDb]);

    useEffect(() => {
        loadMappings(filters);
    }, []);

    useEffect(() => {
        externalSetErrorMessage(errorMessage);
    }, [errorMessage]);

    useEffect(() => {
        if (typeof config?.data?.modules === 'undefined') return;
        if (typeof config.data.modules[configDataProviderName] === 'undefined') return;
        const rawModel = config.data.modules[configDataProviderName].importModel;
        if (rawModel && typeof rawModel === 'object') {
            const newRawModel = Object.values(rawModel).filter(f => f && f.key);
            setModel(newRawModel);
        }
    }, [config, configDataProviderName])


    useEffect(() => {
        setSelectedMappingNewName(selectedMappingNewNameWatcher);
    }, [selectedMappingNewNameWatcher])

    useEffect(() => {
        handleSelectedMapping(selectedMappingWatcher);
    }, [selectedMappingWatcher])

    useEffect(() => {
        setCreateCopy(createCopyWatcher === true);
    }, [createCopyWatcher])

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (actionInvoked === 0) {
            return;
        }

        externalSetActionInvoked(0);

        let newStep = activeStep;

        if (actionInvoked > 0) {
            setAttemptedSteps(prev => ({ ...prev, [activeStep]: true }));
            const validationError = validateStep(activeStep);
            if (validationError) {
                setValidationErrorState(validationError);
                return;
            }
            if (activeStep === 2) {
                handleSubmit(handleFileSubmit)();
                return;
            } else if (activeStep < steps.length - 1) {
                newStep += 1;
            }
        } else if (actionInvoked < 0 && activeStep > 0) {
            newStep -= 1;
        }

        setActiveStep(newStep);
        setValidationErrorState(null);

        externalSetActionLabel(newStep === 2 ? "Submit" : "Next");
        externalSetBackDisabled(newStep === 0);
        externalSetForwardDisabled(newStep === steps.length - 1);

    }, [actionInvoked, uploadedFile, mapping, activeStep, handleSubmit]);


    useEffect(() => {
        externalSetErrorMessage(validationErrorState);
    }, [validationErrorState, externalSetErrorMessage]);

    useEffect(() => {
        const fileInput = document.querySelector('[name="fileUploader"]');
        if (!fileInput) {
            return;
        }
        const f = fileInput.files && fileInput.files[0];
        if (!f) {
            return;
        }
        handleFile(f);
    }, [selectedFileWatcher])

    useEffect(() => {
        if (excelFile !== null) {
            const workbook = XLSX.read(excelFile, { type: 'buffer', codepage: 65001, raw: true });
            const worksheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[worksheetName];
            const data = XLSX.utils.sheet_to_json(worksheet, { defval: "", raw: false, blankrows: true });
            setDataType('xls');
            setData(data);
            setActiveStep(1);
            externalSetBackDisabled(false);
        }
    }, [excelFile]);

    useEffect(() => {
        if (!xmlFile) return;
        const decoder = new TextDecoder("utf-8");
        const xmlString = decoder.decode(xmlFile);
        const parser = new XMLParser();
        const jsonData = parser.parse(xmlString);
        let jsonFlatData;
        let jsonMapping;
        switch (true) {
            case jsonData.hasOwnProperty('FatturaElettronica'):
                jsonFlatData = flattenItalianInvoiceXml(jsonData.FatturaElettronica);
                jsonMapping = mapItalianInvoiceXml();
                setDefaultMapping(jsonMapping);
                break;
            case jsonData.hasOwnProperty('ns3:FatturaElettronica'):
                jsonFlatData = flattenItalianInvoiceXml(jsonData['ns3:FatturaElettronica']);
                jsonMapping = mapItalianInvoiceXml();
                setDefaultMapping(jsonMapping);
                break;
            case jsonData.hasOwnProperty('p:FatturaElettronica'):
                jsonFlatData = flattenItalianInvoiceXml(jsonData['p:FatturaElettronica']);
                jsonMapping = mapItalianInvoiceXml();
                setDefaultMapping(jsonMapping);
                break;
        }

        if (!jsonFlatData) {
            return;
        }
        setDataType('xml');
        setData(jsonFlatData);
        setActiveStep(1);
        externalSetBackDisabled(false);
    }, [xmlFile]);

    const handleFile = (selectedFile) => {
        const validMIMExls = ['application/vnd.ms-excel', 'text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
        const validMIMExml = ['text/xml'];
        const validMIMEpdf = ['application/pdf'];

        if (selectedFile && validMIMExls.includes(selectedFile.type)) {
            resetFiles();
            const url = URL.createObjectURL(selectedFile);
            const uplFile = { extension: selectedFile.name.split('.').pop().toLowerCase(), file: selectedFile, url };
            setUploadedFile(uplFile);
            setError(null);
            externalSetErrorMessage(null);
            const reader = new FileReader();
            reader.readAsArrayBuffer(selectedFile);
            reader.onload = (e) => setExcelFile(e.target.result);
            setValue('filename', selectedFile.name);
        } else if (selectedFile && validMIMExml.includes(selectedFile.type)) {
            resetFiles();
            setError(null);
            externalSetErrorMessage(null);
            const reader = new FileReader();
            reader.readAsArrayBuffer(selectedFile);
            reader.onload = (e) => setXmlFile(e.target.result);
            setValue('filename', selectedFile.name);
        } else if (selectedFile && selectedFile.name.split('.').pop().toLowerCase() === 'p7m') {
            resetFiles(t('P7M file detected'));
            const reader = new FileReader();
            reader.readAsArrayBuffer(selectedFile);
            reader.onload = (e) => {
                setError(t('Parsing P7M file'));
                parseP7MFile(e.target.result);
            };
            setValue('filename', selectedFile.name);
        } else if (selectedFile && validMIMEpdf.includes(selectedFile.type)) {
            resetFiles();
            const url = URL.createObjectURL(selectedFile);
            const pdfFile = { extension: 'pdf', file: selectedFile, url };
            setPdfFile(pdfFile);
            setError(null);
            externalSetErrorMessage(null);
            setValue('filename', selectedFile.name);
            setData(pdfFile);
            setDataType('pdf');
            setActiveStep(1);
            externalSetBackDisabled(false);
        } else {
            const error = selectedFile ? t('Import source file must be a valid file type (xls, xlsx, csv, xml, pdf)') : null;
            resetFiles(error);
        }
    }

    const resetFiles = (error = null) => {
        setError(error);
        setExcelFile(null);
        setUploadedFile(null);
        setXmlFile(null);
        setPdfFile(null);
    }

    const parseP7MFile = (buffer) => {
        const binaryString = new TextDecoder().decode(buffer);
        const xmlMatch = binaryString.match(/<[\w:]*FatturaElettronica[\s\S]*<\/[\w:]*FatturaElettronica>/);
        if (!xmlMatch) {
            setError('Nessun XML valido trovato nel file.');
            return;
        }
        const xmlString = xmlMatch[0].trim();
        try {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
            if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
                setError('Errore nel parsing dell\'XML. Il file potrebbe essere corrotto.');
                return;
            }
            const encoder = new TextEncoder();
            const mtfkr = encoder.encode(xmlString).buffer;
            setXmlFile(mtfkr);
            setError(null);
        } catch (error) {
            setError('Errore durante l\'elaborazione del file.');
        }
    }

    const handleFileSubmit = async (formdata) => {
        if (uploadedFile == null && !pdfFile) {
            setError(t('Missing file'));
            return;
        }
        if (Object.keys(mapping).length === 0 && (dataType === 'xls' || dataType === 'xml')) {
            setError(t('At least one column must be associated to a destinationfield'));
            return;
        }

        setIsSaving(true);
        externalSetErrorMessage(null);

        try {
            let dataToSave = new FormData();
            if (pdfFile) {
                dataToSave.append('file', pdfFile.file);
                dataToSave.append('mapping', false);
                dataToSave.append('excelData', false);
            } else {
                const mappingData = {
                    id: formdata.selectedMapping ?? "",
                    name: formdata.selectedMappingNewName ?? "",
                    createCopy: Boolean(formdata.createCopy),
                    mapping: formdata.mapping,
                    constants: formdata.constants.filter((constant) => constant.field !== "" && constant.value !== "")
                };
                dataToSave.append('file', uploadedFile.file);
                dataToSave.append('mapping', JSON.stringify(mappingData));
            }

            const importDestinationModel = config.data.modules[configDataProviderName].importDestination ?? dataProviderName;
            dataToSave.append('model', importDestinationModel);

            const result = externalFinalSave ? 
                await externalFinalSave(dataToSave).then((data) => {
                    externalFinalSaveThen(data);  
                    handleClose(uid);
                }) : 
                await saveImport(dataToSave).then((data) => handleClose(uid, data.item.data)).catch(() => console.error('catch submit'));

            setActionResult(result.item.data);
            setActiveStep(3);
        } catch (err) {
            console.error('Submission error:', err);
            externalSetErrorMessage(t('An unknown error occurred.'));
        } finally {
            setIsSaving(false);
        }
    }

    const handleSelectedMapping = (v) => {
        const newDefaultMapping = getMappingFromId(v);
        if (typeof newDefaultMapping === 'undefined') {
            setSelectedMapping(null);
            setDefaultMapping({});
            setDefaultConstants([]);
            setValue('createCopy', false);
            return;
        }
        setSelectedMapping(v);
        setDefaultMapping(getMappingFromId(v).mapping ?? {});
        setDefaultConstants(getMappingFromId(v).constants ?? []);
    }

    const getMappingsOptions = () => {
        const options = Object.keys(mappingsDb).length > 0 ? Object.values(mappingsDb).map((v, k) => ({ value: v.id, label: v.name })) : [];
        return options;
    }

    const getMappingFromId = (id) => {
        return mappingsDb.find(obj => obj.id === id);
    }

    const handleOnMappingChange = (newMapping) => {
        setValue('mapping', newMapping);
        setMapping(newMapping);
    }

    const handleOnConstantsChange = (newConstants) => {
        setValue('constants', newConstants);
        setConstants(newConstants);
    }

    const validateStep = (index) => {
        switch (index) {
            case 0:
                if (uploadedFile === null) {
                    return t('Missing file');
                }
                break;
            case 1:
                if ((dataType === 'xls' || dataType === 'xml') && Object.keys(mapping).length === 0) {
                    return t('At least one column must be associated to a destinationfield');
                }
                break;
            default:
        }
        return false;
    }

    return (
        <Box className="importer">

            {/* <JSONPretty data={model} className='json-wrapper' /> */}

            {error && (<Alert sx={{ marginBottom: '15px' }} severity="error">{error}</Alert>)}
            <Stepper activeStep={activeStep} sx={{ marginBottom: '40px', marginTop: '10px' }}>
                {steps.map((label, index) => {
                    const stepProps = {};
                    const labelProps = {};
                    const stepError = attemptedSteps[index] ? validateStep(index) : false;
                    if (stepError) {
                        labelProps.optional = (<Typography variant="caption" color="error">{stepError}</Typography>);
                        labelProps.error = true;
                    }
                    return (
                        <Step key={label} {...stepProps}>
                            <StepLabel {...labelProps}>{t(label)}</StepLabel>
                        </Step>
                    );
                })}
            </Stepper>
            <Box className="importer-form-wrapper">
                <Box className="importer-form">
                    <Input type="hidden" name="mapping" defaultValue={JSON.stringify(mapping)} control={control} editing={true} />
                    <Input type="hidden" name="constants" defaultValue={JSON.stringify(constants)} control={control} editing={true} />
                    <Input type="hidden" name="filename" defaultValue={null} control={control} editing={true} />
                    <Box className={activeStep === 0 ? 'import-stage' : 'import-stage import-stage-hidden'}>
                        <Text level={"title-md"} className="sectionTitle">{t('Select a file to be imported')}</Text>
                        <Input
                            label={'Upload a file'}
                            type="file"
                            name="fileUploader"
                            control={control}
                            editing={true}
                            multiple={false}
                        />
                    </Box>
                    <Box className={activeStep === 1 && data && (dataType === 'xls' || dataType === 'xml') ? 'import-stage' : 'import-stage import-stage-hidden'}>
                        <Text level={"title-md"} className="sectionTitle">{t('Select an existing mapping to be applied')} </Text>
                        <Box className="import-actions">
                            {mappings && Object.keys(mappings).length > 0 && (
                                <Input
                                    label={'Apply saved mapping'}
                                    type="select"
                                    name="selectedMapping"
                                    control={control}
                                    editing={true}
                                    addEmptyOption={true}
                                    options={getMappingsOptions()}
                                    translateOptions={false}
                                />
                            )}
                            {selectedMapping && (
                                <Input
                                    label={'Create a copy of mapping'}
                                    type="select"
                                    name="createCopy"
                                    control={control}
                                    editing={true}
                                    addEmptyOption={true}
                                    options={[{ value: false, label: t('no') }, { value: true, label: t('yes') }]}
                                    translateOptions={false}
                                />
                            )}
                            {(!selectedMapping || selectedMapping && createCopy) && (
                                <Input
                                    label={selectedMapping && createCopy === true ? 'Copy mapping with name' : (selectedMapping) ? 'Rename mapping as' : 'Save mapping as'}
                                    type="text"
                                    name="selectedMappingNewName"
                                    control={control}
                                    editing={true}
                                />
                            )}
                        </Box>
                        <Previewer
                            data={data}
                            model={model}
                            defaultMapping={defaultMapping}
                            onMappingChange={handleOnMappingChange}
                            limit={10}
                        />
                    </Box>
                    <Box className={activeStep === 1 && data && dataType === 'pdf' ? 'import-stage' : 'import-stage import-stage-hidden'}>
                        <Text level={"title-md"} className="sectionTitle">{t('Preview of the uploaded PDF')} </Text>
                        <WidgetDocumentPreview item={pdfFile} downloadable={false} />
                    </Box>
                    <Box className={!skipConstantsStep && activeStep === 2 && data && (dataType === 'xls' || dataType === 'xml') ? 'import-stage' : 'import-stage import-stage-hidden'}>
                        <Text level={"title-md"} className="sectionTitle">Define as many constant values you need to be assigned to each single row</Text>
                        <Text level={"title-sm"} style={{ fontWeight: '300', marginBottom: '30px' }}>Any row containing a value for the given constant field will override the constant</Text>
                        <Constants
                            model={model}
                            control={control}
                            defaultConstants={defaultConstants}
                            onConstantsChange={handleOnConstantsChange}
                            setValue={setValue}
                        />
                        {isSaving && (
                            <Box mt={2} display="flex" justifyContent="center">
                                <CircularProgress />
                            </Box>
                        )}
                    </Box>

                    <Box className={((!skipConstantsStep && activeStep === 3) || (skipConstantsStep && activeStep === 2)) ? 'import-stage' : 'import-stage import-stage-hidden'}>
                        <Text level="title-md">{externalFinalMessage}</Text>
                        {actionResult && (
                            <Box mt={2}>
                                <Typography variant="body2">Risultato: {JSON.stringify(actionResult)}</Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default ImporterFileMappingConstants;